/**
 * useCommunityBattleFeed Hook
 *
 * Fetches community battle feed and subscribes to real-time updates.
 * This is a public endpoint that does not require authentication.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { getApiUrl } from '../config/api';

/**
 * Community battle data structure
 */
export interface CommunityBattle {
  battleId: string;
  battleName: string;
  completedAt: string;
  duration: number;
  player1: { userId: string; displayName: string };
  player2: { userId: string; displayName: string };
  winner: 'player1' | 'player2' | 'draw';
  reason: string;
}

/**
 * API response for community feed
 */
interface CommunityFeedResponse {
  battles: CommunityBattle[];
  total: number;
  hasMore: boolean;
}

/**
 * Hook return type
 */
interface CommunityBattleFeedResult {
  /** Array of completed battles */
  battles: CommunityBattle[];
  /** Total count of battles matching query */
  total: number;
  /** Whether more battles can be loaded */
  hasMore: boolean;
  /** Whether data is being fetched */
  isLoading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Whether the feed is in expanded view */
  isExpanded: boolean;
  /** Current search query */
  searchQuery: string;
  /** Set the search query */
  setSearchQuery: (query: string) => void;
  /** Toggle expanded/collapsed state */
  toggleExpanded: () => void;
  /** Load more battles (pagination) */
  loadMore: () => Promise<void>;
  /** Refetch the battle feed */
  refetch: () => Promise<void>;
  /** Set of battle IDs that are new (for animations) */
  newBattleIds: Set<string>;
}

/**
 * WebSocket event payload for battle completion
 */
interface BattleCompletedEvent {
  battleId: string;
  battleName: string;
  completedAt: string;
  player1DisplayName: string;
  player2DisplayName: string;
  winner: 'player1' | 'player2' | 'draw';
  reason: string;
  duration: number;
}

// getApiUrl imported from config/api

/**
 * useCommunityBattleFeed Hook
 *
 * Fetches community battle feed and subscribes to real-time updates.
 *
 * Features:
 * - Auto-fetches on mount
 * - Real-time updates via WebSocket
 * - Search filtering
 * - Expandable/collapsible view
 * - Load more pagination
 * - Deduplication of battles
 *
 * @param initialLimit - Initial number of battles to show (default: 5)
 * @param maxExpanded - Maximum battles when expanded (default: 20)
 * @returns Object with battles, loading state, error, and control functions
 *
 * @example
 * ```typescript
 * function CommunityBattleFeed() {
 *   const {
 *     battles,
 *     isLoading,
 *     error,
 *     isExpanded,
 *     toggleExpanded,
 *     loadMore,
 *     hasMore
 *   } = useCommunityBattleFeed();
 *
 *   if (isLoading) return <Spinner />;
 *   if (error) return <Error message={error} />;
 *
 *   return (
 *     <div>
 *       {battles.map(battle => (
 *         <BattleCard key={battle.battleId} battle={battle} />
 *       ))}
 *       {hasMore && <button onClick={loadMore}>Load More</button>}
 *       <button onClick={toggleExpanded}>
 *         {isExpanded ? 'Show Less' : 'Show More'}
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useCommunityBattleFeed(
  initialLimit: number = 5,
  maxExpanded: number = 20
): CommunityBattleFeedResult {
  const [battles, setBattles] = useState<CommunityBattle[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [newBattleIds, setNewBattleIds] = useState<Set<string>>(new Set());

  // Track timeout for clearing new battle IDs
  const newBattleTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const currentLimit = isExpanded ? maxExpanded : initialLimit;

  /**
   * Fetch battles from API
   */
  const fetchBattles = useCallback(
    async (limit: number, offset: number = 0, search: string = ''): Promise<CommunityFeedResponse> => {
      const apiUrl = getApiUrl();
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
      });
      if (search) {
        params.append('search', search);
      }

      const response = await fetch(`${apiUrl}/api/battles/community-feed?${params.toString()}`);
      if (!response.ok) {
        throw new Error('Failed to fetch battles');
      }

      return response.json() as Promise<CommunityFeedResponse>;
    },
    []
  );

  /**
   * Initial fetch and refetch
   */
  const refetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchBattles(currentLimit, 0, searchQuery);
      setBattles(data.battles);
      setTotal(data.total);
      setHasMore(data.hasMore);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load battles');
    } finally {
      setIsLoading(false);
    }
  }, [fetchBattles, currentLimit, searchQuery]);

  /**
   * Load more battles (pagination)
   */
  const loadMore = useCallback(async () => {
    if (!hasMore || isLoading) {
      return;
    }

    try {
      const data = await fetchBattles(initialLimit, battles.length, searchQuery);
      setBattles((prev) => [...prev, ...data.battles]);
      setHasMore(data.hasMore);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more');
    }
  }, [fetchBattles, battles.length, hasMore, isLoading, initialLimit, searchQuery]);

  /**
   * Toggle expanded state
   */
  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  /**
   * Fetch on mount and when limit or search changes
   */
  useEffect(() => {
    void refetch();
  }, [refetch]);

  /**
   * WebSocket subscription for real-time updates
   */
  useEffect(() => {
    const apiUrl = getApiUrl();

    // Connect to lobby namespace (no auth required for this public event)
    const socket: Socket = io(`${apiUrl}/lobby`, {
      transports: ['websocket', 'polling'],
    });

    socket.on('connect', () => {
      // Join lobby to receive battle completion events
      socket.emit('lobby:join');
    });

    socket.on('lobby:battle-completed', (event: BattleCompletedEvent) => {
      // Convert event format to CommunityBattle format
      const newBattle: CommunityBattle = {
        battleId: event.battleId,
        battleName: event.battleName,
        completedAt: event.completedAt,
        duration: event.duration,
        player1: { userId: '', displayName: event.player1DisplayName },
        player2: { userId: '', displayName: event.player2DisplayName },
        winner: event.winner,
        reason: event.reason,
      };

      // Prepend to list (if not searching or matches search)
      setBattles((prev) => {
        // Check if already exists (dedupe)
        if (prev.some((b) => b.battleId === newBattle.battleId)) {
          return prev;
        }

        // If searching, check if matches
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          const matches =
            newBattle.player1.displayName.toLowerCase().includes(query) ||
            newBattle.player2.displayName.toLowerCase().includes(query);
          if (!matches) {
            return prev;
          }
        }

        // Track this battle as new for animation
        setNewBattleIds((prevIds) => new Set([...prevIds, newBattle.battleId]));

        // Clear new status after animation duration (3 seconds)
        if (newBattleTimeoutRef.current) {
          clearTimeout(newBattleTimeoutRef.current);
        }
        newBattleTimeoutRef.current = setTimeout(() => {
          setNewBattleIds(new Set());
        }, 3000);

        // Prepend and trim to limit
        return [newBattle, ...prev].slice(0, currentLimit);
      });

      setTotal((prev) => prev + 1);
    });

    return () => {
      socket.disconnect();
      if (newBattleTimeoutRef.current) {
        clearTimeout(newBattleTimeoutRef.current);
      }
    };
  }, [searchQuery, currentLimit]);

  return {
    battles,
    total,
    hasMore,
    isLoading,
    error,
    isExpanded,
    searchQuery,
    setSearchQuery,
    toggleExpanded,
    loadMore,
    refetch,
    newBattleIds,
  };
}
